import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { Box, MenuItem, Checkbox, IconButton } from "@mui/material";
import { C, fmtINR } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdFormField from "../../ipd/utils/IpdFormField";
import IpdButton from "../../ipd/utils/IpdButton";
import IpdSectionCard from "../../ipd/utils/IpdSectionCard";
import IpdDataGrid from "../../ipd/utils/IpdDataGrid";
import SvgIcon from "../SvgIcon";
import { toBillingError } from "../billing-api";
import {
  listLookups,
  listServices,
  createGroup,
  createClassification,
  createSampleType,
  setServiceFlags,
  setServiceRate,
  deleteService,
} from "../service-master-api";
import ServiceMasterServiceScreen from "./ServiceMasterServiceScreen";
import ServiceMasterImportModal from "./ServiceMasterImportModal";

// Format an ISO date (YYYY-MM-DD) as "DD MMM YYYY"; blank values render as an em-dash.
function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Small "add master" modal — shared shape for Group / Classification / Sample ─
function QuickAddModal({ title, fields, onClose, onSubmit }) {
  const [form, setForm] = useState(() => Object.fromEntries(fields.map((f) => [f.key, f.initial || ""])));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const required = fields.filter((f) => f.required && !String(form[f.key] || "").trim());
    if (required.length) { setError(`${required[0].label} is required`); return; }
    setSaving(true); setError("");
    try {
      await onSubmit(form);
      onClose();
    } catch (err) {
      setError(toBillingError(err, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "420px", maxWidth: "100%", background: IPD_COLORS.cardBg, borderRadius: "12px", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "15px", fontWeight: 800, color: C.text }}>{title}</span>
          <IpdButton variant="icon" onClick={onClose} sx={{ minWidth: 0 }}>
            <SvgIcon name="x" size={18} color={C.textSub} />
          </IpdButton>
        </div>
        <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "14px" }}>
          {fields.map((f) => (
            <IpdFormField key={f.key} label={f.label} required={f.required}>
              {f.select ? (
                <IpdTextField select fullWidth value={form[f.key]} onChange={set(f.key)}>
                  <MenuItem value="">{f.placeholder || "Select…"}</MenuItem>
                  {f.options.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                </IpdTextField>
              ) : (
                <IpdTextField fullWidth value={form[f.key]} onChange={set(f.key)} placeholder={f.placeholder} />
              )}
            </IpdFormField>
          ))}
          {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: 600 }}>{error}</div>}
        </div>
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <IpdButton variant="outlined" onClick={onClose}>Cancel</IpdButton>
          <IpdButton onClick={submit} loading={saving}>Save</IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── Rate edit modal — plain edit when charge_modifiable, else confirmed override ─
function RateEditModal({ service, onClose, onSaved }) {
  const [fee, setFee] = useState(String(service.fee ?? ""));
  const [confirmOverride, setConfirmOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const needsOverride = !service.chargeModifiable;

  const submit = async () => {
    if (fee.trim() === "" || Number.isNaN(Number(fee))) { setError("Enter a valid fee"); return; }
    if (needsOverride && !confirmOverride) { setError("Confirm the admin override to edit a locked rate"); return; }
    setSaving(true); setError("");
    try {
      await setServiceRate(service.id, { fee, forceRateEdit: needsOverride });
      toast.success(`Rate updated for ${service.uniqueCode || service.name}`);
      onSaved();
      onClose();
    } catch (err) {
      setError(toBillingError(err, "Could not update rate"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "380px", maxWidth: "100%", background: IPD_COLORS.cardBg, borderRadius: "12px", overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "15px", fontWeight: 800, color: C.text }}>Edit rate</span>
          <IpdButton variant="icon" onClick={onClose} sx={{ minWidth: 0 }}>
            <SvgIcon name="x" size={18} color={C.textSub} />
          </IpdButton>
        </div>
        <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "14px" }}>
          <div style={{ fontSize: "13px", fontWeight: 700, color: C.text }}>{service.uniqueCode || service.serviceCode || service.name}</div>
          <IpdFormField label="Fee (₹)">
            <IpdTextField fullWidth type="number" value={fee} onChange={(e) => setFee(e.target.value)} />
          </IpdFormField>
          {needsOverride && (
            <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "12px", color: C.text, cursor: "pointer" }}>
              <IpdTextField select={false} type="checkbox" value={confirmOverride} onChange={(e) => setConfirmOverride(e.target.checked)} />
              <span>
                This rate is <b>not</b> marked rate-modifiable. Confirm admin override to force the edit
                (sends <code>force_rate_edit</code>).
              </span>
            </label>
          )}
          {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: 600 }}>{error}</div>}
        </div>
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <IpdButton variant="outlined" onClick={onClose}>Cancel</IpdButton>
          <IpdButton onClick={submit} loading={saving}>Save rate</IpdButton>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function ServiceMasterAdmin() {
  const [lookups, setLookups] = useState({ groups: [], classifications: [], sampleTypes: [], bedTypes: [] });
  const [lookupsLoading, setLookupsLoading] = useState(true);

  const [groupId, setGroupId] = useState("");
  const [classificationId, setClassificationId] = useState("");
  const [bedTypeCode, setBedTypeCode] = useState("");
  const [sampleId, setSampleId] = useState("");
  const [chargeModifiable, setChargeModifiable] = useState("");   // "" | "true" | "false"
  const [quantityModifiable, setQuantityModifiable] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [services, setServices] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [drawerMode, setDrawerMode] = useState(null); // null | "create" | "edit"
  const [editingService, setEditingService] = useState(null);
  const [rateEditTarget, setRateEditTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [quickAdd, setQuickAdd] = useState(null); // null | "group" | "classification" | "sample"
  const [busyId, setBusyId] = useState(null);

  const loadLookups = useCallback(async () => {
    setLookupsLoading(true);
    try {
      setLookups(await listLookups({ activeOnly: true }));
    } catch (err) {
      toast.error(toBillingError(err, "Could not load filters"));
    } finally {
      setLookupsLoading(false);
    }
  }, []);

  useEffect(() => { loadLookups(); }, [loadLookups]);

  // Debounce search 300ms.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Cascade: clear classification when it no longer belongs to the selected group.
  useEffect(() => {
    if (!groupId || !classificationId) return;
    const cls = lookups.classifications.find((c) => String(c.id) === String(classificationId));
    if (cls && String(cls.groupId) !== String(groupId)) setClassificationId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const loadServices = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await listServices({
        groupId: groupId || undefined,
        classificationId: classificationId || undefined,
        bedTypeCode: bedTypeCode || undefined,
        sampleId: sampleId || undefined,
        search: search || undefined,
        chargeModifiable: chargeModifiable || undefined,
        quantityModifiable: quantityModifiable || undefined,
        activeOnly: !includeInactive,
        limit: pageSize,
        offset: page * pageSize,
      });
      setServices(res);
    } catch (err) {
      setLoadError(toBillingError(err, "Could not load services"));
      setServices({ items: [], count: 0 });
    } finally {
      setLoading(false);
    }
  }, [groupId, classificationId, bedTypeCode, sampleId, search, chargeModifiable, quantityModifiable, includeInactive, pageSize, page]);

  useEffect(() => { loadServices(); }, [loadServices]);

  const resetPageOnFilterChange = (setter) => (value) => { setter(value); setPage(0); };

  const visibleClassifications = useMemo(
    () => (groupId ? lookups.classifications.filter((c) => String(c.groupId) === String(groupId)) : lookups.classifications),
    [lookups.classifications, groupId]
  );

  const clearFilters = () => {
    setGroupId(""); setClassificationId(""); setBedTypeCode(""); setSampleId("");
    setChargeModifiable(""); setQuantityModifiable(""); setIncludeInactive(false);
    setSearchInput(""); setSearch(""); setPage(0);
  };

  const toggleFlag = async (svc, flag) => {
    setBusyId(svc.id);
    try {
      const next = {
        chargeModifiable: flag === "charge" ? !svc.chargeModifiable : svc.chargeModifiable,
        quantityModifiable: flag === "quantity" ? !svc.quantityModifiable : svc.quantityModifiable,
      };
      await setServiceFlags(svc.id, next);
      await loadServices();
    } catch (err) {
      toast.error(toBillingError(err, "Could not update flags"));
    } finally {
      setBusyId(null);
    }
  };

  const deactivate = async (svc) => {
    if (!window.confirm(`Deactivate "${svc.uniqueCode || svc.name}"? It will drop out of active filters.`)) return;
    setBusyId(svc.id);
    try {
      await deleteService(svc.id);
      toast.success(`${svc.uniqueCode || svc.name} deactivated`);
      await loadServices();
    } catch (err) {
      toast.error(toBillingError(err, "Could not deactivate service"));
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = () => { setEditingService(null); setDrawerMode("create"); };
  const openEdit = (svc) => { setEditingService(svc); setDrawerMode("edit"); };
  const closeDrawer = () => { setDrawerMode(null); setEditingService(null); };
  const handleSaved = async () => { closeDrawer(); await Promise.all([loadServices(), loadLookups()]); };

  // Add/Edit Service is a full screen, not an overlay — it replaces the grid
  // entirely (own back link returns here) rather than floating on top of it.
  if (drawerMode) {
    return (
      <ServiceMasterServiceScreen
        mode={drawerMode}
        service={editingService}
        lookups={lookups}
        onBack={closeDrawer}
        onSaved={handleSaved}
      />
    );
  }

  const { items, count } = services;

  // Auto-fit the unique code column to the longest code on the page (13px / 800 weight ≈ 8.4px per char + cell padding)
  const longestUniqueCode = items.reduce((max, r) => Math.max(max, String(r.uniqueCode || "").length), 0);
  const uniqueCodeWidth = Math.min(360, Math.max(130, Math.ceil(longestUniqueCode * 8.4) + 26));

  const columns = [
    { field: "uniqueCode", headerName: "Unique code", width: uniqueCodeWidth, minWidth: 130, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", fontWeight: 800, color: C.primary, whiteSpace: "nowrap" }}>{p.value || "—"}</Box> },
    { field: "groupName", headerName: "Group", flex: 1, minWidth: 120, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.value || "—"}</Box> },
    { field: "classificationName", headerName: "Classification", flex: 1, minWidth: 130, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub }}>{p.value || "—"}</Box> },
    { field: "name", headerName: "Name", flex: 1.6, minWidth: 180, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "13px", color: C.text }}>{p.value}</Box> },
    { field: "bedTypeCode", headerName: "Bed Type", width: 90, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{p.value || "—"}</Box> },
    { field: "fee", headerName: "Fee", width: 110, sortable: false,
      renderCell: (p) => (
        <IpdButton variant="text" onClick={() => setRateEditTarget(p.row)} sx={{ p: 0, minWidth: 0, fontSize: "13px", fontWeight: 700, color: C.text, textDecoration: "underline", textDecorationStyle: "dotted" }}>
          {p.value != null ? fmtINR(p.value) : "—"}
        </IpdButton>
      ) },
    { field: "chargeModifiable", headerName: "Rate mod.", width: 100, sortable: false, align: "center", headerAlign: "center",
      renderCell: (p) => (
        <Checkbox size="small" checked={Boolean(p.value)} disabled={busyId === p.row.id} onChange={() => toggleFlag(p.row, "charge")} />
      ) },
    { field: "quantityModifiable", headerName: "Qty mod.", width: 100, sortable: false, align: "center", headerAlign: "center",
      renderCell: (p) => (
        <Checkbox size="small" checked={Boolean(p.value)} disabled={busyId === p.row.id} onChange={() => toggleFlag(p.row, "quantity")} />
      ) },
    { field: "cptCode", headerName: "CPT Code", width: 110, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "11px", color: C.textSub, fontFamily: "monospace", whiteSpace: "nowrap" }}>{p.value || "—"}</Box> },
    { field: "effectiveFrom", headerName: "Effective From", width: 130, sortable: false,
      renderCell: (p) => <Box component="span" sx={{ fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtDate(p.value)}</Box> },
    { field: "active", headerName: "Status", width: 100, sortable: false,
      renderCell: (p) => (
        <Box component="span" sx={{ padding: "2px 8px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, background: p.value ? IPD_COLORS.success.bg : IPD_COLORS.pageBg, color: p.value ? IPD_COLORS.success.text : IPD_COLORS.textSecondary }}>
          {p.value ? "Active" : "Inactive"}
        </Box>
      ) },
    { field: "actions", headerName: "", width: 130, sortable: false, align: "right", headerAlign: "right",
      renderCell: (p) => (
        <Box sx={{ whiteSpace: "nowrap" }}>
          <IpdButton variant="outlined" size="small" onClick={() => openEdit(p.row)} startIcon={<SvgIcon name="edit" size={13} color={C.text} />}>
            Edit
          </IpdButton>
          {p.row.active && (
            <IconButton size="small" onClick={() => deactivate(p.row)} disabled={busyId === p.row.id} title="Deactivate" sx={{ ml: "6px" }}>
              <SvgIcon name="trash" size={15} color={C.danger} />
            </IconButton>
          )}
        </Box>
      ) },
  ];

  return (
    <div style={{ animation: "ipdFadeIn 0.2s ease", display: "flex", gap: "20px", alignItems: "flex-start" }}>
      <style>{`@keyframes ipdFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>

      {/* ── LEFT: FILTERS + QUICK-ADD MASTERS ── */}
      <div style={{ width: "260px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "16px" }}>
        <IpdSectionCard title="Filters">
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <IpdFormField label="Search">
              <IpdTextField fullWidth value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="name or code…" />
            </IpdFormField>
            <IpdFormField label="Group">
              <IpdTextField select fullWidth value={groupId} onChange={(e) => resetPageOnFilterChange(setGroupId)(e.target.value)} SelectProps={{ displayEmpty: true }}>
                <MenuItem value="">All groups</MenuItem>
                {lookups.groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Classification">
              <IpdTextField select fullWidth value={classificationId} onChange={(e) => resetPageOnFilterChange(setClassificationId)(e.target.value)} SelectProps={{ displayEmpty: true }}>
                <MenuItem value="">All classifications</MenuItem>
                {visibleClassifications.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Bed type">
              <IpdTextField select fullWidth value={bedTypeCode} onChange={(e) => resetPageOnFilterChange(setBedTypeCode)(e.target.value)} SelectProps={{ displayEmpty: true }}>
                <MenuItem value="">All bed types</MenuItem>
                {lookups.bedTypes.map((b) => <MenuItem key={b.id} value={b.code}>{b.name} ({b.code})</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Sample type">
              <IpdTextField select fullWidth value={sampleId} onChange={(e) => resetPageOnFilterChange(setSampleId)(e.target.value)} SelectProps={{ displayEmpty: true }}>
                <MenuItem value="">All samples</MenuItem>
                {lookups.sampleTypes.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Rate modifiable">
              <IpdTextField select fullWidth value={chargeModifiable} onChange={(e) => resetPageOnFilterChange(setChargeModifiable)(e.target.value)} SelectProps={{ displayEmpty: true }}>
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="true">Modifiable only</MenuItem>
                <MenuItem value="false">Locked only</MenuItem>
              </IpdTextField>
            </IpdFormField>
            <IpdFormField label="Qty modifiable">
              <IpdTextField select fullWidth value={quantityModifiable} onChange={(e) => resetPageOnFilterChange(setQuantityModifiable)(e.target.value)} SelectProps={{ displayEmpty: true }}>
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="true">Modifiable only</MenuItem>
                <MenuItem value="false">Locked only</MenuItem>
              </IpdTextField>
            </IpdFormField>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: C.text, cursor: "pointer" }}>
              <Checkbox size="small" checked={includeInactive} onChange={(e) => resetPageOnFilterChange(setIncludeInactive)(e.target.checked)} sx={{ p: 0 }} />
              Include deactivated
            </label>
            <IpdButton variant="outlined" size="small" onClick={clearFilters} sx={{ justifyContent: "center" }}>Clear filters</IpdButton>
          </div>
        </IpdSectionCard>

        <IpdSectionCard title="Quick-add masters">
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <IpdButton variant="outlined" size="small" onClick={() => setQuickAdd("group")} startIcon={<SvgIcon name="plus" size={13} color={C.text} />} sx={{ justifyContent: "flex-start" }}>
              Group
            </IpdButton>
            <IpdButton variant="outlined" size="small" onClick={() => setQuickAdd("classification")} disabled={lookupsLoading} startIcon={<SvgIcon name="plus" size={13} color={C.text} />} sx={{ justifyContent: "flex-start" }}>
              Classification
            </IpdButton>
            <IpdButton variant="outlined" size="small" onClick={() => setQuickAdd("sample")} startIcon={<SvgIcon name="plus" size={13} color={C.text} />} sx={{ justifyContent: "flex-start" }}>
              Sample type
            </IpdButton>
          </div>
        </IpdSectionCard>
      </div>

      {/* ── CENTER: GRID ── */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "14px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
          <div style={{ fontSize: "13px", color: C.textSub }}>
            {loading ? "Loading…" : `${count} service${count === 1 ? "" : "s"}`}
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <IpdButton variant="outlined" onClick={() => setShowImport(true)} startIcon={<SvgIcon name="upload" size={15} color={C.text} />}>
              Import CSV
            </IpdButton>
            <IpdButton onClick={openCreate} startIcon={<SvgIcon name="plus" size={15} color={IPD_COLORS.white} />}>
              Add service
            </IpdButton>
          </div>
        </div>

        <IpdDataGrid
          rows={items}
          columns={columns}
          loading={loading}
          error={loadError}
          onRetry={loadServices}
          getRowId={(r) => r.id}
          disableColumnMenu
          emptyMessage="No services for this filter"
          emptyDescription="Clear the filters or import a service CSV to populate the master."
          emptyAction={
            <IpdButton variant="outlined" onClick={() => setShowImport(true)} startIcon={<SvgIcon name="upload" size={15} color={C.text} />}>
              Import CSV
            </IpdButton>
          }
          paginationMode="server"
          rowCount={count}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            if (model.pageSize !== pageSize) { setPageSize(model.pageSize); setPage(0); }
            else setPage(model.page);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          getRowClassName={(p) => (p.row.active ? "" : "ipd-row-inactive")}
          sx={{ border: "none", "& .ipd-row-inactive": { opacity: 0.55 } }}
        />
      </div>

      {rateEditTarget && (
        <RateEditModal service={rateEditTarget} onClose={() => setRateEditTarget(null)} onSaved={loadServices} />
      )}

      {showImport && (
        <ServiceMasterImportModal onClose={() => setShowImport(false)} onImported={async () => { setShowImport(false); await Promise.all([loadServices(), loadLookups()]); }} />
      )}

      {quickAdd === "group" && (
        <QuickAddModal
          title="Add service group"
          fields={[
            { key: "group_code", label: "Group code", placeholder: "ONCOLOGY", required: true },
            { key: "name", label: "Name", placeholder: "Oncology", required: true },
            { key: "description", label: "Description", placeholder: "Cancer services" },
          ]}
          onClose={() => setQuickAdd(null)}
          onSubmit={async (form) => {
            await createGroup({ group_code: form.group_code.trim(), name: form.name.trim(), description: form.description?.trim() || "" });
            toast.success(`Group "${form.name}" added`);
            await loadLookups();
          }}
        />
      )}

      {quickAdd === "classification" && (
        <QuickAddModal
          title="Add classification"
          fields={[
            { key: "group_id", label: "Group", select: true, options: lookups.groups, required: true },
            { key: "classification_code", label: "Classification code", placeholder: "PET", required: true },
            { key: "name", label: "Name", placeholder: "PET Scan", required: true },
          ]}
          onClose={() => setQuickAdd(null)}
          onSubmit={async (form) => {
            await createClassification({ group_id: form.group_id, classification_code: form.classification_code.trim(), name: form.name.trim() });
            toast.success(`Classification "${form.name}" added`);
            await loadLookups();
          }}
        />
      )}

      {quickAdd === "sample" && (
        <QuickAddModal
          title="Add sample type"
          fields={[
            { key: "name", label: "Name", placeholder: "EDTA Whole Blood", required: true },
            { key: "sample_code", label: "Sample code", placeholder: "EDTA-WHOLE-BLOOD", required: true },
          ]}
          onClose={() => setQuickAdd(null)}
          onSubmit={async (form) => {
            await createSampleType({ name: form.name.trim(), sample_code: form.sample_code.trim() });
            toast.success(`Sample type "${form.name}" added`);
            await loadLookups();
          }}
        />
      )}
    </div>
  );
}
